/**
 * Descriptors da ponte com o dashboard da TemAPI.
 *
 *  - `dashboardCard`  — um indicador, gráfico ou tabela dentro de um painel;
 *  - `dashboardEmbed` — um dashboard existente inteiro, por id.
 *
 * Ver `model/dashboard-schema.ts` para o porquê de ser ponte e não catálogo novo.
 */
import { ScreenNode } from '../../model/node-props';
import { ComponentDescriptor, defineDescriptor } from '../component-descriptor';
import { buildLayoutGroup } from './common-groups';

/**
 * Onde um campo pode morar.
 *
 * `tab` entrou depois do relato "esta exigindo que sempre tenha um painel":
 * uma aba com um campo so nao precisa de painel nenhum, e obrigar a criar um
 * antes e a mesma friccao que fazia toda tela sair parecendo o mesmo cadastro.
 * O painel continua sendo o normal quando ha muitos campos.
 */
const FIELD_PARENTS = ['panel', 'row', 'section', 'tab'] as const;

/** A query só interessa quando o cartão busca os próprios dados. */
function usesOwnQuery(node: ScreenNode): boolean {
  const source = (node.props as { source?: string }).source;
  return source === undefined || source === 'query';
}

/** O caminho de dados só interessa no caso oposto. */
function usesScreenData(node: ScreenNode): boolean {
  const source = (node.props as { source?: string }).source;
  return source === 'dataPath';
}

// ---------------------------------------------------------------- cartao

export const dashboardCardDescriptor = defineDescriptor<'dashboardCard'>({
  type: 'dashboardCard',
  labelKey: 'builderScreen.palette.dashboardCard',
  descriptionKey: 'builderScreen.palette.dashboardCard.desc',
  icon: 'bar-chart',
  category: 'data',
  level: 'simple',

  createDefault: context => ({
    props: {
      kind: 'chart',
      source: 'query',
      minHeight: 240
    },
    layout: { order: context.order, colSpan: 12, rowSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.inspector.label',
          level: 'simple'
        },
        {
          kind: 'enum',
          path: 'kind',
          labelKey: 'builderScreen.dashboard.cardKind',
          helpKey: 'builderScreen.dashboard.cardKind.help',
          level: 'simple',
          options: [
            { value: 'minicard', labelKey: 'builderScreen.dashboard.kind.minicard' },
            { value: 'chart', labelKey: 'builderScreen.dashboard.kind.chart' },
            { value: 'table', labelKey: 'builderScreen.dashboard.kind.table' }
          ]
        },
        {
          // Editor próprio: reusa as telas de configuração que o dashboard já
          // tem (mini-card-configuration, chart-card-configuration,
          // table-card-configuration), escolhidas conforme `kind`.
          kind: 'custom',
          path: 'card',
          editor: 'dashboardCardConfig',
          labelKey: 'builderScreen.dashboard.cardConfig',
          helpKey: 'builderScreen.dashboard.cardConfig.help',
          level: 'simple'
        }
      ]
    },
    {
      labelKey: 'builderScreen.dashboard.dataSection',
      level: 'simple',
      properties: [
        {
          kind: 'enum',
          path: 'source',
          labelKey: 'builderScreen.dashboard.source',
          helpKey: 'builderScreen.dashboard.source.help',
          level: 'simple',
          options: [
            { value: 'query', labelKey: 'builderScreen.dashboard.source.query' },
            { value: 'dataPath', labelKey: 'builderScreen.dashboard.source.dataPath' }
          ]
        },
        {
          kind: 'dataPath',
          path: 'dataPath',
          target: 'binding',
          labelKey: 'builderScreen.inspector.dataPath',
          helpKey: 'builderScreen.dashboard.dataPath.help',
          level: 'simple',
          visibleWhen: usesScreenData
        },
        {
          // Editor próprio: lê os @PARAM da query do cartão e oferece, para
          // cada um, o campo da tela que o alimenta.
          kind: 'custom',
          path: 'filters',
          editor: 'dashboardFilterBindings',
          labelKey: 'builderScreen.dashboard.filters',
          helpKey: 'builderScreen.dashboard.filters.help',
          level: 'simple',
          visibleWhen: usesOwnQuery
        }
      ]
    },
    {
      labelKey: 'builderScreen.groups.behavior',
      level: 'advanced',
      properties: [
        {
          kind: 'number',
          path: 'refreshSeconds',
          labelKey: 'builderScreen.dashboard.refreshSeconds',
          helpKey: 'builderScreen.dashboard.refreshSeconds.help',
          level: 'advanced',
          min: 0,
          visibleWhen: usesOwnQuery
        },
        {
          kind: 'number',
          path: 'minHeight',
          labelKey: 'builderScreen.dashboard.minHeight',
          helpKey: 'builderScreen.dashboard.minHeight.help',
          level: 'advanced',
          min: 0
        }
      ]
    },
    buildLayoutGroup()
  ]
});

// ---------------------------------------------------------------- dashboard inteiro

export const dashboardEmbedDescriptor = defineDescriptor<'dashboardEmbed'>({
  type: 'dashboardEmbed',
  labelKey: 'builderScreen.palette.dashboardEmbed',
  descriptionKey: 'builderScreen.palette.dashboardEmbed.desc',
  icon: 'dashboard',
  category: 'data',
  level: 'simple',

  createDefault: context => ({
    props: {
      showFilter: true,
      showToolbar: false,
      minHeight: 420
    },
    layout: { order: context.order, colSpan: 24, rowSpan: 10 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.inspector.label',
          level: 'simple'
        },
        {
          // Editor próprio: lista os dashboards de Mktpdashboard em vez de
          // pedir o número na mão.
          kind: 'custom',
          path: 'dashboardId',
          editor: 'dashboardPicker',
          labelKey: 'builderScreen.dashboard.dashboardId',
          helpKey: 'builderScreen.dashboard.dashboardId.help',
          level: 'simple'
        },
        {
          kind: 'text',
          path: 'initialTabName',
          labelKey: 'builderScreen.dashboard.initialTab',
          helpKey: 'builderScreen.dashboard.initialTab.help',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.dashboard.dataSection',
      level: 'advanced',
      properties: [
        {
          kind: 'custom',
          path: 'filters',
          editor: 'dashboardFilterBindings',
          labelKey: 'builderScreen.dashboard.filters',
          helpKey: 'builderScreen.dashboard.embedFilters.help',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.groups.behavior',
      level: 'simple',
      properties: [
        {
          kind: 'boolean',
          path: 'showFilter',
          labelKey: 'builderScreen.dashboard.showFilter',
          helpKey: 'builderScreen.dashboard.showFilter.help',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'showToolbar',
          labelKey: 'builderScreen.dashboard.showToolbar',
          level: 'advanced'
        },
        {
          kind: 'number',
          path: 'minHeight',
          labelKey: 'builderScreen.dashboard.minHeight',
          helpKey: 'builderScreen.dashboard.minHeight.help',
          level: 'advanced',
          min: 0
        }
      ]
    },
    buildLayoutGroup()
  ]
});

export const DASHBOARD_DESCRIPTORS: readonly ComponentDescriptor[] = [
  dashboardCardDescriptor,
  dashboardEmbedDescriptor
];
